import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, BarChart2 } from 'lucide-react';
import { investments, portfolioHistory } from '../../../data/mockData';
const timeframes = ['1W', '1M', '3M', '1Y', 'ALL'] as const;
type Timeframe = (typeof timeframes)[number];
const typeColors: Record<string, string> = {
  stock: '#1566FF',
  etf: '#39E75F',
  crypto: '#F59E0B'
};
const allocationData = [{
  name: 'Stocks',
  value: 7050,
  color: '#1566FF'
}, {
  name: 'ETFs',
  value: 12353,
  color: '#39E75F'
}, {
  name: 'Crypto',
  value: 42397,
  color: '#F59E0B'
}];
export function InvestmentsView() {
  const [timeframe, setTimeframe] = useState<Timeframe>('1M');
  const totalValue = investments.reduce((s, i) => s + i.value, 0);
  const totalChange = investments.reduce((s, i) => s + i.change * i.shares, 0);
  const totalChangePercent = totalChange / (totalValue - totalChange) * 100;
  const chartData = portfolioHistory[timeframe];
  return <div className="space-y-6" data-id="element-409">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4" data-id="element-410">
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-411">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-412">
            Portfolio Value
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-413">
            ${totalValue.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-414">Total invested</p>
        </div>
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-415">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-416">
            Today's Change
          </p>
          <div className="flex items-center gap-2" data-id="element-417">
            <p className="text-2xl font-bold text-accent-600 dark:text-accent-400" data-id="element-418">
              +${totalChange.toFixed(2)}
            </p>
            <TrendingUp size={18} className="text-accent-500" data-id="element-419" />
          </div>
          <p className="text-xs text-accent-600 dark:text-accent-400 mt-1" data-id="element-420">
            +{totalChangePercent.toFixed(2)}% today
          </p>
        </div>
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-421">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-422">
            Total Return
          </p>
          <p className="text-2xl font-bold text-accent-600 dark:text-accent-400" data-id="element-423">
            +$23,799
          </p>
          <p className="text-xs text-accent-600 dark:text-accent-400 mt-1" data-id="element-424">
            +62.8% all time
          </p>
        </div>
      </div>

      {/* Portfolio chart */}
      <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-425">
        <div className="flex items-center justify-between mb-4" data-id="element-426">
          <div data-id="element-427">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-428">
              Portfolio Performance
            </h3>
            <p className="text-xs text-gray-400" data-id="element-429">Historical value</p>
          </div>
          <div className="flex gap-1 bg-gray-100 dark:bg-dark-hover rounded-xl p-1" data-id="element-430">
            {timeframes.map(tf => <button key={tf} onClick={() => setTimeframe(tf)} className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${timeframe === tf ? 'bg-white dark:bg-dark-card text-primary-600 dark:text-primary-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`} data-id="element-431">
                {tf}
              </button>)}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220} data-id="element-432">
          <AreaChart data={chartData} data-id="element-433">
            <defs data-id="element-434">
              <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-435">
                <stop offset="5%" stopColor="#1566FF" stopOpacity={0.2} data-id="element-436" />
                <stop offset="95%" stopColor="#1566FF" stopOpacity={0} data-id="element-437" />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{
            fontSize: 11,
            fill: '#9CA3AF'
          }} axisLine={false} tickLine={false} data-id="element-438" />
            <YAxis tick={{
            fontSize: 11,
            fill: '#9CA3AF'
          }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} domain={['auto', 'auto']} data-id="element-439" />
            <Tooltip formatter={(v: any) => [`$${Number(v).toLocaleString()}`, 'Value']} contentStyle={{
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            fontSize: '12px'
          }} data-id="element-440" />
            <Area type="monotone" dataKey="value" stroke="#1566FF" strokeWidth={2.5} fill="url(#portfolioGrad)" data-id="element-441" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Holdings + Allocation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-442">
        {/* Holdings table */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-card rounded-2xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden" data-id="element-443">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-border" data-id="element-444">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-445">
              Holdings
            </h3>
          </div>
          <table className="w-full" data-id="element-446">
            <thead data-id="element-447">
              <tr className="border-b border-gray-50 dark:border-dark-border" data-id="element-448">
                {['Asset', 'Price', 'Shares', 'Value', 'Change'].map(h => <th key={h} className="text-left px-5 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide" data-id="element-449">
                    {h}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-border" data-id="element-450">
              {investments.map(inv => <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors" data-id="element-451">
                  <td className="px-5 py-3" data-id="element-452">
                    <div className="flex items-center gap-2" data-id="element-453">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{
                    backgroundColor: typeColors[inv.type]
                  }} data-id="element-454">
                        {inv.symbol.slice(0, 2)}
                      </div>
                      <div data-id="element-455">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-id="element-456">
                          {inv.symbol}
                        </p>
                        <p className="text-xs text-gray-400 truncate max-w-[100px]" data-id="element-457">
                          {inv.name.split(' ').slice(0, 2).join(' ')}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700 dark:text-gray-300" data-id="element-458">
                    ${inv.price.toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700 dark:text-gray-300" data-id="element-459">
                    {inv.shares}
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold text-gray-800 dark:text-gray-200" data-id="element-460">
                    ${inv.value.toLocaleString()}
                  </td>
                  <td className="px-5 py-3" data-id="element-461">
                    <span className={`text-xs font-semibold flex items-center gap-1 ${inv.changePercent >= 0 ? 'text-accent-600 dark:text-accent-400' : 'text-red-500'}`} data-id="element-462">
                      {inv.changePercent >= 0 ? <TrendingUp size={11} data-id="element-463" /> : <TrendingDown size={11} data-id="element-464" />}
                      {inv.changePercent >= 0 ? '+' : ''}
                      {inv.changePercent.toFixed(2)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Allocation */}
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-465">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1" data-id="element-466">
            Asset Allocation
          </h3>
          <p className="text-xs text-gray-400 mb-4" data-id="element-467">By asset class</p>
          <ResponsiveContainer width="100%" height={160} data-id="element-468">
            <PieChart data-id="element-469">
              <Pie data={allocationData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value" data-id="element-470">
                {allocationData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-471" />)}
              </Pie>
              <Tooltip formatter={(v: any) => [`$${Number(v).toLocaleString()}`, '']} contentStyle={{
              borderRadius: '8px',
              border: 'none',
              fontSize: '11px'
            }} data-id="element-472" />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-3" data-id="element-473">
            {allocationData.map((a, i) => <div key={i} className="flex items-center justify-between" data-id="element-474">
                <div className="flex items-center gap-2" data-id="element-475">
                  <div className="w-2.5 h-2.5 rounded-full" style={{
                backgroundColor: a.color
              }} data-id="element-476" />
                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-477">
                    {a.name}
                  </span>
                </div>
                <div className="text-right" data-id="element-478">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300" data-id="element-479">
                    ${a.value.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-400 ml-1" data-id="element-480">
                    ({Math.round(a.value / totalValue * 100)}%)
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}